import React, { useState, useEffect, useCallback, useRef } from "react"
import { Trash2, Plus, Minus } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useCart } from "@/hooks/useCart"
import { useDebounce } from "@/hooks/useDebounce"
import { formatToDevanagariStyle } from "@/utilities/helper"

export const Cart: React.FC = () => {
  const {
    cart,
    isLoading,
    removeItem,
    updateQuantityAsync,
    checkout,
    isCheckingOut,
  } = useCart()
  const navigate = useNavigate()
  const [localQuantities, setLocalQuantities] = useState<{
    [key: number]: number
  }>({})
  const [updatingItemId, setUpdatingItemId] = useState<number | null>(null)
  const lastSyncedQuantitiesRef = useRef<{ [key: number]: number }>({})

  const cartItems = cart?.items || []

  // Initialize local quantities from cart
  useEffect(() => {
    if (cartItems.length > 0) {
      const initialQuantities: { [key: number]: number } = {}
      cartItems.forEach((item: any) => {
        initialQuantities[item.id] = item.quantity
      })
      setLocalQuantities(initialQuantities)
      lastSyncedQuantitiesRef.current = { ...initialQuantities }
    }
  }, [cartItems.length]) // Only update when number of items changes

  // Debounce quantity updates
  const debouncedQuantities = useDebounce(localQuantities, 500)

  // Sync debounced quantities with server
  useEffect(() => {
    if (!cartItems.length || Object.keys(debouncedQuantities).length === 0)
      return

    const syncQuantities = async () => {
      for (const [cartItemIdStr, quantity] of Object.entries(
        debouncedQuantities
      )) {
        const cartItemId = parseInt(cartItemIdStr)
        const lastSynced = lastSyncedQuantitiesRef.current[cartItemId]
        const cartItem = cartItems.find((item: any) => item.id === cartItemId)

        if (
          cartItem &&
          quantity !== lastSynced &&
          quantity !== cartItem.quantity
        ) {
          setUpdatingItemId(cartItemId)
          try {
            await updateQuantityAsync({
              cartItemId: cartItemId,
              quantity: quantity,
            })
            lastSyncedQuantitiesRef.current[cartItemId] = quantity
          } catch (error) {
            // Error is handled in the hook, revert to server quantity
            setLocalQuantities((prev) => ({
              ...prev,
              [cartItemId]: cartItem.quantity,
            }))
            lastSyncedQuantitiesRef.current[cartItemId] = cartItem.quantity
          } finally {
            setUpdatingItemId(null)
          }
        }
      }
    }

    syncQuantities()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuantities])

  const subtotal = cartItems.reduce((sum: number, item: any) => {
    const quantity = localQuantities[item.id] ?? item.quantity
    return sum + (item.product?.sell_price || 0) * quantity
  }, 0)

  const handleCheckout = async () => {
    try {
      await checkout()
      navigate("/website/orders")
    } catch (error) {
      // Error is handled in the hook
    }
  }

  const handleQuantityChange = useCallback(
    (cartItemId: number, delta: number) => {
      const cartItem = cartItems.find((item: any) => item.id === cartItemId)
      if (!cartItem) return

      const currentQuantity = localQuantities[cartItemId] ?? cartItem.quantity
      const maxQuantity = cartItem.product?.stock || 0
      const newQuantity = Math.max(
        1,
        Math.min(maxQuantity, currentQuantity + delta)
      )

      setLocalQuantities((prev) => ({
        ...prev,
        [cartItemId]: newQuantity,
      }))
    },
    [cartItems, localQuantities]
  )

  if (isLoading) {
    return (
      <div className="bg-gray-50 min-h-screen py-10 flex items-center justify-center">
        <div className="text-gray-500">Loading cart...</div>
      </div>
    )
  }

  if (!cart || cartItems.length === 0) {
    return (
      <div className="bg-gray-50 min-h-screen py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">
            Shopping Cart
          </h1>
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-gray-500 text-lg">Your cart is empty</p>
            <button
              onClick={() => navigate("/website")}
              className="mt-4 bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-50 min-h-screen py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Shopping Cart</h1>

        <div className="lg:grid lg:grid-cols-12 lg:gap-x-12 lg:items-start">
          {/* Cart Items List */}
          <section className="lg:col-span-7 bg-white rounded-lg shadow-sm p-6">
            <ul className="divide-y divide-gray-200">
              {cartItems.map((item: any) => {
                const displayQuantity =
                  localQuantities[item.id] ?? item.quantity
                const maxQuantity = item.product?.stock || 0
                const itemTotal =
                  (item.product?.sell_price || 0) * displayQuantity
                const isUpdating = updatingItemId === item.id

                return (
                  <li key={item.id} className="flex py-6">
                    <div className="h-24 w-24 flex-shrink-0 overflow-hidden rounded-md border border-gray-200 bg-gray-100">
                      {item.product?.thumbnail ? (
                        <img
                          src={item.product.thumbnail.url}
                          alt={item.product.name}
                          className="h-full w-full object-cover object-center"
                        />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-gray-400">
                          No Image
                        </div>
                      )}
                    </div>
                    <div className="ml-4 flex flex-1 flex-col">
                      <div>
                        <div className="flex justify-between text-base font-medium text-gray-900">
                          <h3 className="text-md">
                            {item.product?.name || "Product"}
                          </h3>
                          <p className="ml-4">
                            Rs. {formatToDevanagariStyle(itemTotal)}
                          </p>
                        </div>
                        <p className="mt-1 text-sm text-gray-500">
                          Rs.{" "}
                          {formatToDevanagariStyle(item.product?.sell_price) ||
                            0}{" "}
                          each
                        </p>
                      </div>

                      <div className="flex flex-1 items-end justify-between text-sm mt-3">
                        <div className="flex items-center border rounded-md">
                          <button
                            onClick={() => handleQuantityChange(item.id, -1)}
                            disabled={isUpdating || displayQuantity <= 1}
                            className="p-2 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Minus size={16} />
                          </button>
                          <span className="px-2 min-w-[24px] text-center flex items-center justify-center">
                            {displayQuantity}
                          </span>
                          <button
                            onClick={() => handleQuantityChange(item.id, 1)}
                            disabled={
                              isUpdating || displayQuantity >= maxQuantity
                            }
                            className="p-2 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeItem(item.id)}
                          className="font-medium text-red-600 hover:text-red-500 flex items-center"
                        >
                          <Trash2 size={16} className="mr-1" /> Remove
                        </button>
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
          </section>

          {/* Order Summary */}
          <section className="lg:col-span-5 mt-16 lg:mt-0 bg-white rounded-lg shadow-sm p-6 sticky top-24">
            <h2 className="text-lg font-medium text-gray-900">Order Summary</h2>
            <dl className="mt-6 space-y-4">
              <div className="flex items-center justify-between">
                <dt className="text-sm text-gray-600">Subtotal</dt>
                <dd className="text-sm font-medium text-gray-900">
                  Rs. {formatToDevanagariStyle(subtotal)}
                </dd>
              </div>
              <div className="flex items-center justify-between border-t border-gray-200 pt-4">
                <dt className="text-base font-medium text-gray-900">
                  Order total
                </dt>
                <dd className="text-base font-medium text-gray-900">
                  Rs. {formatToDevanagariStyle(subtotal)}
                </dd>
              </div>
            </dl>
            <div className="mt-6">
              <button
                onClick={handleCheckout}
                disabled={isCheckingOut || cartItems.length === 0}
                className="w-full bg-indigo-600 border border-transparent rounded-md shadow-sm py-3 px-4 text-base font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCheckingOut ? "Processing..." : "Checkout"}
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
